import { Env } from '@/libs/env';

const TIMEZONE_PATTERN = /^UTC[+-]\d{2}:\d{2}$/i;
const DEFAULT_TIMEZONE = 'UTC+08:00';

export interface footerAnimateConfig {
  /** 是否开启底部 APNG 动画 */
  enabled: boolean;
  /** 命中时执行动画的底部跳转链接正则 */
  urlPattern: string;
  /** 生效时间 [startDate, endDate]（yyyy-mm-dd），可为空数组 */
  activeDateRange: string[];
  /** 时区（UTC±HH:MM），默认 UTC+08:00 */
  timezone: string;
}

export interface GlobalConfig {
  traceUserIds: string[];
  /** userId 后两位采样桶，"00"-"99" 两位字符串格式，命中则 force_send */
  traceSuffixes: string[];
  footerAnimateConfig?: footerAnimateConfig;
  /** 兼容旧版 CDN 配置 key，后续可移除 */
  footerApngConfig?: footerAnimateConfig;
}

// 模块级单例，用于非 React 上下文（埋点等）同步读取
let _traceUserIds: string[] = [];
let _traceSuffixes: string[] = [];

export function getTraceUserIds(): string[] {
  return _traceUserIds;
}

export function getTraceSuffixes(): string[] {
  return _traceSuffixes;
}

/**
 * 规范化 footerAnimateConfig，供 Jotai atom 使用
 * - 配置缺失、未启用或 urlPattern 为空时返回 null（表示不展示动画）
 * - 返回非 null 时 enabled 恒为 true，可直接参与动画逻辑判断
 */
export function normalizefooterAnimateConfig(config?: footerAnimateConfig | null): footerAnimateConfig | null {
  if (!config || !config.enabled) return null;

  const pattern = typeof config.urlPattern === 'string' ? config.urlPattern.trim() : '';
  if (!pattern) return null;

  const range = Array.isArray(config.activeDateRange)
    ? config.activeDateRange
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .slice(0, 2)
    : [];

  const timezone =
    typeof config.timezone === 'string' && TIMEZONE_PATTERN.test(config.timezone)
      ? config.timezone.toUpperCase()
      : DEFAULT_TIMEZONE;

  return {
    enabled: true,
    urlPattern: pattern,
    activeDateRange: range,
    timezone,
  };
}

function buildConfigUrl(): string {
  const base = Env.CDN_HOST || '';
  return `${base}/global-config.json?_t=${Date.now()}`;
}

/**
 * 拉取 global-config.json 并同步模块级单例（traceUserIds / traceSuffixes）
 * - 生产/预发：从 CDN_HOST 拉取（CDN_HOST 非空）
 * - 本地开发：从本地 /global-config.json 拉取（CDN_HOST 为空）
 * - 加载失败时返回 null，不影响应用运行
 */
export async function loadGlobalConfig(): Promise<GlobalConfig | null> {
  try {
    const res = await fetch(buildConfigUrl(), { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data: GlobalConfig = await res.json();

    _traceUserIds = data.traceUserIds ?? [];
    _traceSuffixes = data.traceSuffixes ?? [];

    return data;
  } catch {
    return null;
  }
}
